//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Wizard finalise page
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                  Initial Revision.
//    002   24.11.23 Sean Flook                  Moved Box to @mui/system.
//    003   30.04.24 Sean Flook        IMANN-384 Set the updating flag on the ADSWizardAddressList.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    004   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    005   24.06.25 Sean Flook       IMANN-1839 Display the progress when creating the properties.
//    006   22.07.25 Sean Flook       IMANN-1855 Use the new ADSCircularProgress component.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    007   13.06.25 Sean Flook       IMANN-1812 Set the initial extent for the map.
//    008   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    009   27.10.25 Sean Flook       IMANN-2092 Use the new spatialReferenceWkid property from mapContext to set the spatialReference used by the layers in the map.
//    010   06.11.25 Sean Flook       IMANN-2092 Correctly set the spatialReferenceWkid variable.
//    011   17.12.25 Sean Flook       IMANN-2196 Handle closing of the selection control.
//    012   16.03.26 Sean Flook       IMANN-2327 Pass through the onMapUpdated function to prevent the Finish button from being enabled prematurely.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import MapContext from "../context/mapContext";
import SettingsContext from "../context/settingsContext";
import SelectionContext from "../context/selectionContext";

import { getAuthorityExtent } from "./../utils/HelperUtils";

import { Grid, Box } from "@mui/material";
import ADSMapDialog from "../components/ADSMapDialog";
import ADSWizardAddressList from "../components/ADSWizardAddressList";
import ADSCircularProgress from "../components/ADSCircularProgress";

import { dataFormStyle } from "./../utils/ADSStyles";

WizardFinalisePage.propTypes = {
  data: PropTypes.array.isRequired,
  isChild: PropTypes.bool,
  errors: PropTypes.array,
  creating: PropTypes.bool,
  progress: PropTypes.number,
  onDataChange: PropTypes.func.isRequired,
  onErrorChanged: PropTypes.func.isRequired,
  onMapUpdated: PropTypes.func.isRequired,
};

function WizardFinalisePage({
  data,
  isChild = false,
  errors,
  creating = false,
  progress = 0,
  onDataChange,
  onErrorChanged,
  onMapUpdated,
}) {
  const mapContext = useContext(MapContext);
  const settingsContext = useContext(SettingsContext);
  const selectionContext = useContext(SelectionContext);

  const [checked, setChecked] = useState([]);

  const spatialReferenceWkid = useRef(mapContext.spatialReference || 27700);

  /**
   * Event to handle updating the list of checked records.
   *
   * @param {Array} updatedChecked The list of checked records.
   */
  const handleCheckedChanged = (updatedChecked) => {
    setChecked(updatedChecked);
  };

  /**
   * Event to handle when the data has been changed.
   *
   * @param {object} updatedData The updated data.
   */
  const handleDataChanged = (updatedData) => {
    if (onDataChange) onDataChange(updatedData);
  };

  /**
   * Event to handle when the list of errors changes.
   *
   * @param {Array} finaliseErrors The list of errors.
   */
  const handleErrorChanged = (finaliseErrors) => {
    if (onErrorChanged) onErrorChanged(finaliseErrors);
  };

  useEffect(() => {
    if (mapContext.currentPinSelected) setChecked([mapContext.currentPinSelected]);
    else setChecked([]);
  }, [mapContext.currentPinSelected]);

  useEffect(() => {
    if (selectionContext.closeControl) {
      selectionContext.onControlClosed();
      setChecked([]);
      mapContext.onHighlightClear();
    }
  }, [selectionContext, mapContext]);

  return (
    <Box id="wizard-finalise-page" sx={dataFormStyle("ADSMapDialogWizard")}>
      <Grid container justifyContent="flex-start" spacing={0}>
        <Grid size={12}>
          <Grid container spacing={0} justifyContent="flex-start">
            <Grid size={4}>
              <ADSWizardAddressList
                data={data}
                checked={checked}
                errors={errors}
                updating={creating}
                onCheckedChanged={handleCheckedChanged}
                onDataChanged={handleDataChanged}
                onErrorChanged={handleErrorChanged}
              />
            </Grid>
            <Grid size={8}>
              <ADSMapDialog
                startExtent={
                  mapContext.currentExtent
                    ? { ...mapContext.currentExtent, spatialReference: { wkid: spatialReferenceWkid.current } }
                    : getAuthorityExtent(
                        settingsContext.authorityCode,
                        settingsContext.isScottish,
                        settingsContext.isWelsh,
                        spatialReferenceWkid.current
                      )
                }
                data={data}
                isChild={isChild}
                isRange
                onMapUpdated={onMapUpdated}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {creating && (
        <ADSCircularProgress display={creating} progress={progress} failedCount={errors.length} displayPercentage />
      )}
    </Box>
  );
}

export default WizardFinalisePage;
